import { defineSlotRecipe } from "@pandacss/dev";
import {
  DECORATIVE_COLORS,
  DecorativeColors,
  getDecorativeColorToken,
} from "./utils/getDecorativeColorToken";
import { SlotRecipeCompoundVariant } from "@zenchef/styled-system/types";

const contextualIconSlots = ["root", "svg"] as const;

const getDecorativeColorCompoundVariants = (
  color: DecorativeColors,
): SlotRecipeCompoundVariant<
  (typeof contextualIconSlots)[number],
  {
    hierarchy: "subtlest" | "subtle" | "bold" | "bolder";
    colorTheme: DecorativeColors;
  }
>[] => [
  {
    hierarchy: "subtlest",
    colorTheme: color,
    css: {
      root: { color: getDecorativeColorToken("content", color) },
      svg: { stroke: getDecorativeColorToken("border", color) },
    },
  },
  {
    hierarchy: "subtle",
    colorTheme: color,
    css: {
      svg: {
        stroke: getDecorativeColorToken("background", color, "base-subtle"),
      },
      root: {
        color: getDecorativeColorToken("content", color),
        background: getDecorativeColorToken("background", color, "base-subtle"),
      },
    },
  },
  {
    hierarchy: "bold",
    colorTheme: color,
    css: {
      root: {
        background: getDecorativeColorToken("background", color, "base-bold"),
      },
      svg: {
        stroke: getDecorativeColorToken("background", color, "base-bold"),
      },
    },
  },
  {
    hierarchy: "bolder",
    colorTheme: color,
    css: {
      root: {
        background: getDecorativeColorToken("background", color, "base-bolder"),
      },
      svg: {
        stroke: getDecorativeColorToken("background", color, "base-bolder"),
      },
    },
  },
];

export const aromaContextualIcon = defineSlotRecipe({
  className: "aroma-contextual-icon",
  jsx: ["ContextualIcon"],
  slots: contextualIconSlots,
  base: {
    root: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "content.neutral.reverse.bold",
      transition: "all 0.3s ease-in-out",
      clipPath: "url(#clipShape)",
      _aromaticonChildren: {
        zIndex: "1",
      },
    },
    svg: {
      position: "absolute",
      zIndex: "0",
      transition: "all 0.3s ease-in-out",
      fill: "transparent",
    },
  },
  variants: {
    size: {
      small: {
        root: {
          height: "s",
          width: "s",
          minHeight: "s",
          minWidth: "s",
          _aromaticonChildren: {
            fontSize: "{sizes.3xs}",
          },
        },
      },
      medium: {
        root: {
          height: "m",
          width: "m",
          minHeight: "m",
          minWidth: "m",
          _aromaticonChildren: {
            fontSize: "{sizes.2xs}",
          },
        },
      },
      large: {
        root: {
          height: "xl",
          width: "xl",
          minHeight: "xl",
          minWidth: "xl",
          _aromaticonChildren: {
            fontSize: "{sizes.xs}",
          },
        },
      },
      xlarge: {
        root: {
          height: "2xl",
          width: "2xl",
          minHeight: "2xl",
          minWidth: "2xl",
          _aromaticonChildren: {
            fontSize: "{sizes.s}",
          },
        },
      },
      jumbo: {
        root: {
          height: "3xl",
          width: "3xl",
          minHeight: "3xl",
          minWidth: "3xl",
          _aromaticonChildren: {
            fontSize: "{sizes.l}",
          },
        },
      },
    },
    colorTheme: {
      brand: {},
      neutral: {},
      red: {},
      orange: {},
      yellow: {},
      green: {},
      blue: {},
      purple: {},
      pink: {},
      ai: {},
      transparent: {},
    },
    hierarchy: {
      subtlest: {
        root: {
          background: "background.neutral.base.subtlest",
        },
        svg: {
          strokeWidth: "1px",
        },
      },
      subtle: {
        svg: {
          strokeWidth: "0",
        },
      },
      bold: {
        svg: {
          strokeWidth: "0",
        },
      },
      bolder: {
        svg: {
          strokeWidth: "0",
        },
      },
    },
  },
  compoundVariants: [
    {
      hierarchy: "bolder",
      colorTheme: "transparent",
      css: {
        svg: { fill: "transparent" },
      },
    },
    {
      hierarchy: "subtlest",
      colorTheme: "brand",
      css: {
        root: { color: "content.brand.bold" },
        svg: { stroke: "border.brand-bold" },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "brand",
      css: {
        root: {
          color: "content.brand.bold",
          background: "background.brand.subtle",
        },
        svg: {
          stroke: "background.brand.subtle",
        },
      },
    },
    {
      hierarchy: "bold",
      colorTheme: "brand",
      css: {
        root: {
          background: "background.brand.bold",
        },
        svg: {
          stroke: "background.brand.bold",
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "brand",
      css: {
        root: {
          background: "background.brand.bold-hover",
        },
        svg: {
          stroke: "background.brand.bold-hover",
        },
      },
    },
    //AI
    {
      hierarchy: "subtlest",
      colorTheme: "ai",
      css: {
        root: {
          "& i": {
            background: "content.ai",
            color: "transparent",
            backgroundClip: "text",
          },
        },
        svg: {
          fontSize: "63",
          stroke: "border.decorative.purple",
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "ai",
      css: {
        root: {
          background: "background.ai.subtle",
          "& i": {
            background: "content.ai",
            color: "transparent",
            backgroundClip: "text",
          },
        },
        svg: {
          stroke: "background.ai.bold",
        },
      },
    },
    {
      //Special case, BOLD AI is not possible
      hierarchy: "bold",
      colorTheme: "ai",
      css: {
        root: { display: "none" },
        svg: { display: "none" },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "ai",
      css: {
        root: {
          background: "background.ai.bold",
        },
        svg: {
          stroke: "background.ai.bold",
        },
      },
    },
    {
      hierarchy: "subtlest",
      colorTheme: "neutral",
      css: {
        root: { color: "content.neutral.base.subtlest" },
        svg: { stroke: "border.neutral.bold" },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "neutral",
      css: {
        root: {
          color: "content.neutral.base.subtlest",
          background: "background.decorative.neutral.base-subtle",
        },
        svg: {
          stroke: "background.decorative.neutral.base-subtle",
        },
      },
    },
    {
      hierarchy: "bold",
      colorTheme: "neutral",
      css: {
        root: {
          background: "background.decorative.neutral.base-bold",
        },
        svg: {
          stroke: "background.decorative.neutral.base-bold",
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "neutral",
      css: {
        root: {
          background: "background.decorative.neutral.base-bolder",
        },
        svg: {
          stroke: "background.decorative.neutral.base-bolder",
        },
      },
    },
    ...DECORATIVE_COLORS.flatMap(getDecorativeColorCompoundVariants),
  ],
  defaultVariants: {
    size: "medium",
    colorTheme: "neutral",
    hierarchy: "bold",
  },
});
